<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import ProtocolsAPI from 'dashboard/api/protocols';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

// State
const protocol = ref(null);
const protocolSteps = ref([]);
const contactProtocols = ref([]);
const isLoading = ref(false);
const isLinking = ref(false);

// Pick existing
const showProtocolPicker = ref(false);
const selectedProtocolId = ref(null);

// Create new
const showCreateForm = ref(false);
const isCreating = ref(false);
const newForm = ref({ title: '', protocol_category_id: null, protocol_subcategory_id: null, team_id: null, user_id: null });
const skipUserReset = ref(false);
const categories = computed(() => store.getters['protocols/getCategories'] || []);
const subcategories = computed(() => {
  if (!newForm.value.protocol_category_id) return [];
  const cat = categories.value.find(c => c.id === Number(newForm.value.protocol_category_id));
  return cat?.subcategories || [];
});
const teams = computed(() => store.getters['teams/getTeams'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);

// Filtered agents based on selected team
const filteredAgents = computed(() => {
  const teamId = newForm.value.team_id;
  if (!teamId) return agents.value;
  const members = store.getters['teamMembers/getTeamMembers'](teamId);
  if (!members || !members.length) return agents.value;
  const memberIds = members.map(m => m.id);
  return agents.value.filter(a => memberIds.includes(a.id));
});

const chatProtocolId = computed(() => currentChat.value?.protocol_id);
const contactId = computed(() => currentChat.value?.meta?.sender?.id);

const fetchProtocol = async () => {
  if (!chatProtocolId.value) {
    protocol.value = null;
    protocolSteps.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await ProtocolsAPI.show(chatProtocolId.value);
    protocol.value = data;
    await fetchSteps();
  } catch {
    protocol.value = null;
  } finally {
    isLoading.value = false;
  }
};

const fetchSteps = async () => {
  if (!protocol.value) return;
  try {
    const { data } = await ProtocolsAPI.getSteps(protocol.value.id);
    protocolSteps.value = data || [];
  } catch {
    protocolSteps.value = [];
  }
};

const toggleStep = async step => {
  try {
    const newStatus = step.status === 1 ? 0 : 1;
    await ProtocolsAPI.updateStep(protocol.value.id, step.id, {
      status: newStatus,
    });
    step.status = newStatus;
  } catch {
    // revert or ignore
  }
};

const fetchContactProtocols = async () => {
  if (!contactId.value) return;
  try {
    const { data } = await ProtocolsAPI.get({ user_id: contactId.value }); // wait, how to fetch by contact? The backend uses `requester_type` and `requester_id` for contact. We can just fetch all protocols for now, or filter by requester.
    // Let's just fetch all or recent ones for simplicity, or search.
    // Actually, ProtocolsAPI.get() with search could be better.
    // But let's assume we just get the latest 50 protocols for the whole account for the dropdown if we don't have a contact filter.
    const response = await ProtocolsAPI.get({ per: 50 });
    contactProtocols.value = response.data.payload || [];
  } catch {
    contactProtocols.value = [];
  }
};

const loadDependencies = async () => {
  try {
    store.dispatch('teams/get');
    store.dispatch('agents/get');
    store.dispatch('protocols/getCategories');
  } catch {}
};

// When team changes, fetch its members and reset user selection
watch(() => newForm.value.team_id, (teamId) => {
  if (skipUserReset.value) return;
  newForm.value.user_id = '';
  if (teamId) {
    store.dispatch('teamMembers/get', { teamId });
  }
});

// Linking
const openPicker = () => {
  showProtocolPicker.value = true;
  selectedProtocolId.value = null;
  fetchContactProtocols();
};
const cancelPicker = () => {
  showProtocolPicker.value = false;
  selectedProtocolId.value = null;
};
const linkToProtocol = async () => {
  if (!selectedProtocolId.value || isLinking.value) return;
  isLinking.value = true;
  try {
    if (chatProtocolId.value) {
      await ProtocolsAPI.unlinkConversation(
        chatProtocolId.value,
        props.conversationId
      );
    }
    await ProtocolsAPI.linkConversation(
      selectedProtocolId.value,
      props.conversationId
    );
    store.commit('updateConversationProtocolId', {
      conversationId: currentChat.value.id,
      protocolId: Number(selectedProtocolId.value),
    });
    showProtocolPicker.value = false;
    await fetchProtocol();
  } catch {
  } finally {
    isLinking.value = false;
  }
};
const unlinkProtocol = async () => {
  if (!chatProtocolId.value || isLinking.value) return;
  isLinking.value = true;
  try {
    await ProtocolsAPI.unlinkConversation(
      chatProtocolId.value,
      props.conversationId
    );
    store.commit('updateConversationProtocolId', {
      conversationId: currentChat.value.id,
      protocolId: null,
    });
    protocol.value = null;
    protocolSteps.value = [];
  } catch {
  } finally {
    isLinking.value = false;
  }
};

// Creating
const openCreateForm = async () => {
  showCreateForm.value = true;
  showProtocolPicker.value = false;
  skipUserReset.value = true;
  const teamId = currentChat.value?.meta?.team?.id || '';
  newForm.value = { title: '', protocol_category_id: '', protocol_subcategory_id: '', team_id: teamId, user_id: currentChat.value?.meta?.assignee?.id || '' };
  await loadDependencies();
  if (teamId) {
    store.dispatch('teamMembers/get', { teamId });
  }
  nextTick(() => { skipUserReset.value = false; });
};
const cancelCreateForm = () => {
  showCreateForm.value = false;
};
const createAndLink = async () => {
  if (!newForm.value.title || isCreating.value) return;
  isCreating.value = true;
  try {
    const payload = {
      title: newForm.value.title,
      team_id: newForm.value.team_id || null,
      user_id: newForm.value.user_id || null,
      protocol_category_id: newForm.value.protocol_category_id || null,
      protocol_subcategory_id: newForm.value.protocol_subcategory_id || null,
      requester_type: 'Contact',
      requester_id: contactId.value,
    };
    // Pass conversation_id alongside the protocol payload so the backend links in one request
    const createdProtocol = await store.dispatch('protocols/create', {
      protocol: payload,
      conversation_id: currentChat.value.id,
    });
    const newProtocolId = createdProtocol?.id;
    if (newProtocolId) {
      store.commit('updateConversationProtocolId', {
        conversationId: currentChat.value.id,
        protocolId: newProtocolId,
      });
      showCreateForm.value = false;
      await fetchProtocol();
    }
  } catch (err) {
    console.error('[Protocol] createAndLink failed:', err);
  } finally {
    isCreating.value = false;
  }
};

watch(() => newForm.value.protocol_category_id, () => {
  newForm.value.protocol_subcategory_id = '';
});

watch(chatProtocolId, () => fetchProtocol(), { immediate: false });

onMounted(() => {
  fetchProtocol();
});
</script>

<template>
  <div class="px-1 py-2">
    <div v-if="isLoading" class="flex items-center justify-center py-6">
      <div class="flex items-center gap-2">
        <span class="i-lucide-loader-2 animate-spin text-n-slate-9" />
        <span class="text-xs text-n-slate-10">Carregando protocolo...</span>
      </div>
    </div>

    <div v-else-if="!chatProtocolId || !protocol">
      <div
        v-if="!showProtocolPicker && !showCreateForm"
        class="flex flex-col items-center justify-center gap-3 py-4"
      >
        <div
          class="flex size-10 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <span class="i-lucide-file-text size-5 text-n-slate-9" />
        </div>
        <span class="text-xs text-n-slate-10">Nenhum protocolo vinculado</span>
        <div class="flex gap-2">
          <button
            class="cursor-pointer rounded-lg bg-n-brand/10 px-4 py-1.5 text-xs font-semibold text-n-brand transition-all duration-150 hover:bg-n-brand/20 active:bg-n-brand/30"
            @click="openPicker"
          >
            + Vincular
          </button>
          <button
            class="cursor-pointer rounded-lg bg-n-brand/10 px-4 py-1.5 text-xs font-semibold text-n-brand transition-all duration-150 hover:bg-n-brand/20 active:bg-n-brand/30"
            @click="openCreateForm"
          >
            + Criar
          </button>
        </div>
      </div>

      <div
        v-if="showCreateForm"
        class="flex flex-col gap-1.5 rounded-lg border border-n-weak bg-n-solid-2 p-2.5 shadow-sm"
      >
        <label
          class="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-n-slate-11"
        >
          <span class="i-lucide-plus-circle size-3.5" />
          Criar Novo Protocolo
        </label>
        <div>
          <label class="mb-0.5 block text-[10px] font-medium text-n-slate-10"
            >Título *</label
          >
          <input
            v-model="newForm.title"
            class="w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs text-n-slate-12 transition-all placeholder:text-n-slate-8 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
            placeholder="Ex: Atendimento Suporte..."
          />
        </div>
        <div>
          <label class="mb-0.5 block text-[10px] font-medium text-n-slate-10"
            >Equipe</label
          >
          <div class="relative">
            <select
              v-model="newForm.team_id"
              class="w-full appearance-none rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs text-n-slate-12 transition-all cursor-pointer hover:border-n-slate-7 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
              style="background-image: none"
            >
              <option value="">Sem equipe</option>
              <option v-for="t in teams" :key="t.id" :value="t.id">
                {{ t.name }}
              </option>
            </select>
            <span class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9 pointer-events-none" />
          </div>
        </div>
        <div>
          <label class="mb-0.5 block text-[10px] font-medium text-n-slate-10"
            >Responsável</label
          >
          <div class="relative">
            <select
              v-model="newForm.user_id"
              class="w-full appearance-none rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs text-n-slate-12 transition-all cursor-pointer hover:border-n-slate-7 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
              style="background-image: none"
            >
              <option value="">Sem responsável</option>
              <option v-for="a in filteredAgents" :key="a.id" :value="a.id">
                {{ a.name }}
              </option>
            </select>
            <span class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9 pointer-events-none" />
          </div>
        </div>
        <div>
          <label class="mb-0.5 block text-[10px] font-medium text-n-slate-10"
            >Categoria</label
          >
          <div class="relative">
            <select
              v-model="newForm.protocol_category_id"
              class="w-full appearance-none rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs text-n-slate-12 transition-all cursor-pointer hover:border-n-slate-7 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
              style="background-image: none"
            >
              <option value="">Nenhuma</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">
                {{ c.name }}
              </option>
            </select>
            <span class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9 pointer-events-none" />
          </div>
        </div>
        <div v-if="subcategories.length > 0">
          <label class="mb-0.5 block text-[10px] font-medium text-n-slate-10"
            >Subcategoria</label
          >
          <div class="relative">
            <select
              v-model="newForm.protocol_subcategory_id"
              class="w-full appearance-none rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs text-n-slate-12 transition-all cursor-pointer hover:border-n-slate-7 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
              style="background-image: none"
            >
              <option value="">Nenhuma</option>
              <option v-for="s in subcategories" :key="s.id" :value="s.id">
                {{ s.name }}
              </option>
            </select>
            <span class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9 pointer-events-none" />
          </div>
        </div>
        <div class="flex gap-2 mt-1">
          <button
            :disabled="!newForm.title || isCreating"
            class="flex-1 rounded-lg bg-n-brand px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-n-brand-hover disabled:cursor-not-allowed disabled:opacity-40"
            @click="createAndLink"
          >
            {{ isCreating ? 'Criando...' : 'Criar e Vincular' }}
          </button>
          <button
            class="rounded-lg bg-n-alpha-3 px-3 py-2 text-xs font-medium text-n-slate-11 transition-all hover:bg-n-alpha-4"
            @click="cancelCreateForm"
          >
            Cancelar
          </button>
        </div>
      </div>

      <div
        v-if="showProtocolPicker"
        class="flex flex-col gap-2.5 rounded-lg border border-n-weak bg-n-solid-2 p-3 shadow-sm"
      >
        <label
          class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
        >
          Selecionar Protocolo Existente
        </label>
        <div class="relative">
          <select
            v-model="selectedProtocolId"
            class="w-full appearance-none rounded-lg border border-n-weak bg-n-background px-3 py-2 text-xs text-n-slate-12 transition-all cursor-pointer hover:border-n-slate-7 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
            style="background-image: none"
          >
            <option :value="null">Escolha um protocolo...</option>
            <option v-for="p in contactProtocols" :key="p.id" :value="p.id">
              #{{ p.display_id }} - {{ p.title }}
            </option>
          </select>
          <span class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9 pointer-events-none" />
        </div>
        <div class="flex gap-2 pt-1">
          <button
            :disabled="!selectedProtocolId || isLinking"
            class="flex-1 rounded-lg bg-n-brand px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-n-brand-hover disabled:cursor-not-allowed disabled:opacity-40"
            @click="linkToProtocol"
          >
            Vincular
          </button>
          <button
            class="rounded-lg bg-n-alpha-3 px-3 py-2 text-xs font-medium text-n-slate-11 transition-all hover:bg-n-alpha-4"
            @click="cancelPicker"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col gap-0">
      <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3 shadow-sm">
        <div class="mb-2 flex items-center gap-2">
          <div
            class="flex size-7 shrink-0 items-center justify-center rounded-lg"
            :class="{
              'bg-n-amber-2': protocol.status === 'pending',
              'bg-n-blue-2': protocol.status === 'in_progress',
              'bg-n-teal-2': protocol.status === 'completed',
            }"
          >
            <span
              class="i-lucide-file-text size-3.5"
              :class="{
                'text-n-amber-11': protocol.status === 'pending',
                'text-n-blue-11': protocol.status === 'in_progress',
                'text-n-teal-11': protocol.status === 'completed',
              }"
            />
          </div>
          <div class="min-w-0 flex-1">
            <p
              class="truncate text-sm font-semibold leading-tight text-n-slate-12"
            >
              {{ protocol.title }}
            </p>
            <p class="mt-0.5 text-[10px] leading-tight text-n-slate-10">
              #{{ protocol.display_id }}
            </p>
          </div>
          <button
            class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-10 transition-all hover:bg-n-brand/10 hover:text-n-brand"
            title="Ver protocolo"
            @click="router.push('/app/accounts/' + store.getters.getCurrentAccountId + '/protocols')"
          >
            <span class="i-lucide-external-link size-3.5" />
          </button>
        </div>
        <div class="mb-2 flex items-center gap-1.5">
          <button
            class="rounded-md bg-n-alpha-2 px-2 py-1 text-[10px] font-medium text-n-slate-11 transition-all hover:bg-n-alpha-3 hover:text-n-slate-12"
            @click="openPicker"
          >
            Trocar
          </button>
          <button
            class="rounded-md bg-n-ruby-2 px-2 py-1 text-[10px] font-medium text-n-ruby-10 transition-all hover:bg-n-ruby-3 hover:text-n-ruby-11"
            @click="unlinkProtocol"
          >
            Desvincular
          </button>
        </div>

        <div class="mb-2.5 flex items-center gap-2">
          <span
            class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
            :class="{
              'bg-n-amber-2 text-n-amber-11': protocol.status === 'pending',
              'bg-n-blue-2 text-n-blue-11': protocol.status === 'in_progress',
              'bg-n-teal-2 text-n-teal-11': protocol.status === 'completed',
            }"
          >
            <span
              class="size-1.5 rounded-full"
              :class="{
                'bg-n-amber-9': protocol.status === 'pending',
                'bg-n-blue-9': protocol.status === 'in_progress',
                'bg-n-teal-9': protocol.status === 'completed',
              }"
            />
            {{
              protocol.status === 'pending'
                ? 'Pendente'
                : protocol.status === 'in_progress'
                ? 'Em Andamento'
                : 'Concluído'
            }}
          </span>
        </div>

        <div v-if="protocolSteps.length > 0" class="mt-3 border-t border-n-weak pt-3">
          <p class="mb-2 text-[10px] font-semibold uppercase tracking-wider text-n-slate-10">
            Checklist ({{ protocolSteps.filter(s => s.status === 1).length }}/{{ protocolSteps.length }})
          </p>
          <div class="flex flex-col gap-1.5">
            <button
              v-for="step in protocolSteps"
              :key="step.id"
              class="flex items-start gap-2 text-left group"
              @click="toggleStep(step)"
            >
              <div
                class="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border transition-colors"
                :class="
                  step.status === 1
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-n-strong bg-n-background group-hover:border-n-brand'
                "
              >
                <span v-if="step.status === 1" class="i-lucide-check size-3" />
              </div>
              <span
                class="text-xs transition-colors"
                :class="
                  step.status === 1
                    ? 'text-n-slate-9 line-through'
                    : 'text-n-slate-12 group-hover:text-n-brand'
                "
              >
                {{ step.title }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
